import React, { useState } from "react";
import useSWR from "swr";
import { Snackbar } from "@material-ui/core";
import { Redirect } from "react-router";
import WarningIcon from "@mui/icons-material/Warning";

import StoreFrontIcon from "../../img/storefront.svg";
import StoreFrontSelectedIcon from "../../img/storefront_selected.svg";
import MapPinIcon from "../../img/map_pin.svg";
import MapPinSelectedIcon from "../../img/map_pin_selected.svg";
import MapGPSArrowTerritoryIcon from "../../img/map_gps_arrow_territory.svg";
import MapGPSArrowTerritorySelectedIcon from "../../img/map_gps_arrow_territory_selected.svg";
import AltRouteIcon from "../../img/alt_route.svg";
import AltRouteSelectedIcon from "../../img/alt_route_selected.svg";
import MapIcon from "../../img/map.svg";
import MapSelectedIcon from "../../img/map_selected.svg";

import HierarchyFilterTab from "./components//HierarchyFilterTab";
import MetricConfigurationCard, {
  MetricSkeletonLoader,
} from "./components/MetricConfigurationCard";
import ErrorCard from "../InventoryManagement/components/Common/ErrorCard";

import { KPIDashboardService } from "../KPIDashboard/service/kpiDashboardService";
import { useNotification } from "../Notifications/NotificationProvider";
import { USER_TYPES } from "../UserList/utils/userConfigs";

import "./index.scss";

const hierarchyList = [
  {
    id: 1,
    label: "MDT Channel Head",
    value: USER_TYPES.BKASH_SUPER_USER,
    icon: StoreFrontIcon,
    selectedIcon: StoreFrontSelectedIcon,
  },
  {
    id: 2,
    label: "Circle Head",
    value: USER_TYPES.CIRCLE_HEAD,
    icon: MapPinIcon,
    selectedIcon: MapPinSelectedIcon,
  },
  {
    id: 3,
    label: "Zonal Managers",
    value: USER_TYPES.REGIONAL_MANAGER,
    icon: MapGPSArrowTerritoryIcon,
    selectedIcon: MapGPSArrowTerritorySelectedIcon,
  },
  {
    id: 4,
    label: "Area Managers",
    value: USER_TYPES.AREA_MANAGER,
    icon: AltRouteIcon,
    selectedIcon: AltRouteSelectedIcon,
  },
  {
    id: 5,
    label: "Territory Managers",
    value: USER_TYPES.TERRITORY_MANAGER,
    icon: MapIcon,
    selectedIcon: MapSelectedIcon,
  },
  {
    id: 6,
    label: "Operations Managers",
    value: USER_TYPES.AGENCY_USER,
    icon: StoreFrontIcon,
    selectedIcon: StoreFrontSelectedIcon,
  },
];

const KPIMetricConfiguration = () => {
  const [selectedHierarchy, setSelectedHierarchy] = useState(
    USER_TYPES.BKASH_SUPER_USER
  );
  const [savingConfiguration, setSavingConfiguration] = useState(false);
  const [showWeightChangeWarning, setShowWeightChangeWarning] = useState(false);
  const dispatchNotification = useNotification();

  const { data, error, mutate } = useSWR(
    selectedHierarchy,
    () =>
      KPIDashboardService.metricConfigurationFetcher({
        role: selectedHierarchy,
      }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const loadingConfiguration = !data && !error;

  const onConfigurationSave = async (updatedWeights) => {
    setSavingConfiguration(true);
    try {
      const response = await KPIDashboardService.saveConfiguration({
        role: selectedHierarchy,
        configuration: updatedWeights,
      });
      dispatchNotification({
        type: "SUCCESS",
        message: response?.message || "Configuration saved successfully",
      });
      setShowWeightChangeWarning(true);
      mutate();
    } catch (error) {
      console.error("Error saving configuration:", error);
      dispatchNotification({
        type: "ERROR",
        message:
          error?.message || "Failed to save configuration. Please try again.",
      });
    } finally {
      setSavingConfiguration(false);
    }
  };

  const localUserToken = JSON.parse(localStorage.getItem("currUserToken"));
  if (!localUserToken) return <Redirect to="/signin" />;

  return (
    <div className="main-container">
      <div className="kpi-page-header">
        <div className="page-title">
          <h1>KPI Configuration Dashboard</h1>
        </div>
      </div>
      <div className="kpi-metric-configuration">
        <HierarchyFilterTab
          tabList={hierarchyList}
          selectedHierarchy={selectedHierarchy}
          onHierarchySelection={setSelectedHierarchy}
        />
        <h2 className="configuration-title">
          Set KPI Weights For{" "}
          {hierarchyList.find((h) => h.value === selectedHierarchy)?.label ||
            "the selected hierarchy"}
        </h2>
        <Snackbar
          open={showWeightChangeWarning}
          onClose={() => setShowWeightChangeWarning(false)}
          slots={{ transition: "Slide" }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          key="configuration-update-notification"
          autoHideDuration={15000}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              background: "#FFF4E5",
              color: "#663C00",
              padding: "16px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: 500,
              fontFamily: "Roboto",
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              maxWidth: "420px",
              borderLeft: "6px solid #FFA726",
            }}
          >
            <WarningIcon
              style={{
                color: "#FFA726",
                height: "22px",
                width: "22px",
                flexShrink: 0,
              }}
            />
            Please update the configuration for other hierarchies to ensure the
            total weights for each add up to 100%.
          </div>
        </Snackbar>
        {error ? (
          <ErrorCard
            title="Failed to load Metric Configuration"
            message="An error occurred while fetching the metric configuration. Please try again later."
            style={{
              background: "#FFFFFF",
              borderRadius: "12px",
              minHeight: "60vh",
            }}
          />
        ) : loadingConfiguration ? (
          <MetricSkeletonLoader />
        ) : (
          <MetricConfigurationCard
            configurationData={data || { metrics: [] }}
            onConfigurationSave={onConfigurationSave}
            savingConfiguration={savingConfiguration}
          />
        )}
      </div>
    </div>
  );
};

export default KPIMetricConfiguration;
